import axiosInstance from '@/libs/axios'

/**
 * Chave do cache local da última resolução de parcelamento, por loja e tabela de preço.
 *
 * O PDV opera sem comunicação com alguma frequência, e nesse estado o operador ainda precisa
 * oferecer parcelamento. A última resolução recebida para aquele par (loja, tabela) é a melhor
 * aproximação disponível — mas é uma aproximação, e a tela precisa dizer isso.
 */
const CACHE_KEY = 'echope:pdv:installment-options'

// Inclui o caixa: a restrição por PDV (`sale.installment.pay_boxes_enabled`) pode liberar um
// caixa e bloquear outro na mesma loja e tabela, e a retaguarda offline não pode misturar os dois.
const buildScopeKey = (storeId, priceTableId, payBoxId) =>
  `${storeId ?? 'null'}:${priceTableId ?? 'null'}:${payBoxId ?? 'null'}`

const readCache = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}')
  } catch (error) {
    return {}
  }
}

const writeCache = cache => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache))
  } catch (error) {
    // Cache indisponível não pode impedir a venda: o pagamento à vista continua funcionando.
  }
}

const getInitialState = () => ({
  /** Resolução vigente para a venda em andamento. Nulo significa parcelamento indisponível. */
  resolution: null,

  /** Verdadeiro quando a resolução veio do cache local, e não da API. */
  fromCache: false,

  loading: false,
})

export default {
  namespaced: true,
  state: getInitialState(),

  getters: {
    /**
     * Opções oferecidas ao operador. Vazio significa que só existe pagamento à vista — e isso não
     * é erro: é o estado normal enquanto o parcelamento não estiver configurado para a loja.
     */
    installmentOptions(state) {
      return state.resolution?.options || []
    },

    hasInstallmentOptions(state, getters) {
      return getters.installmentOptions.some(option => option.installments > 1)
    },

    allowedModes(state) {
      return state.resolution?.allowedModes || []
    },

    defaultMode(state) {
      return state.resolution?.defaultMode || null
    },

    /**
     * Quando a política repassa o acréscimo, a frente de venda precisa exibir o valor acrescido e
     * o total sem acréscimo de forma visível ao cliente — exigência da Lei nº 13.455/2017, que
     * permite a diferenciação por prazo desde que informada.
     */
    applySurchargeToCustomer(state) {
      return state.resolution?.applySurchargeToCustomer === true
    },

    /**
     * Busca a opção pela quantidade de parcelas.
     *
     * A comparação é numérica de propósito: o Vue Formulate normaliza os valores das opções de
     * `vue-select` para STRING, então o `installmentCount` do formulário chega aqui como "6"
     * enquanto a opção da API traz o número 6. Uma comparação estrita devolveria null, e o efeito
     * seria silencioso — o valor cobrado nunca seria aplicado ao pagamento.
     */
    optionByInstallments: (state, getters) => installments =>
      getters.installmentOptions.find(
        option => Number(option.installments) === Number(installments)
      ) || null,

  },

  mutations: {
    SET_RESOLUTION(state, { resolution, fromCache }) {
      state.resolution = resolution
      state.fromCache = fromCache === true
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    CLEAN_STATE(state) {
      Object.assign(state, getInitialState())
    },
  },

  actions: {
    /**
     * Consulta as opções de parcelamento para a venda em andamento.
     *
     * A consulta é por ESCOPO — loja, tabela de preço e valor — e não pela venda: no PDV a venda
     * ainda não existe quando o operador escolhe a forma de pagamento, porque o caixa monta o
     * carrinho localmente e só persiste no registro do pagamento.
     *
     * Falha de comunicação não interrompe a venda: cai para a última resolução conhecida daquele
     * escopo e sinaliza que o dado pode estar desatualizado. Sem resolução prévia, o parcelamento
     * fica indisponível e a venda segue à vista.
     */
    async fetchInstallmentOptions({ commit, rootGetters }, { saleValue }) {
      const payBoxConfiguration =
        rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']

      const storeId = payBoxConfiguration?.store?.id ?? null

      // O caixa vai na consulta porque o parâmetro `sale.installment.pay_boxes_enabled` pode
      // restringir o parcelamento a um conjunto de PDVs, para piloto ou liberação gradual.
      const payBoxId = payBoxConfiguration?.id ?? null

      const priceTableId = rootGetters['pages/pdv/payBox/salePriceTableId'] ?? null
      const scopeKey = buildScopeKey(storeId, priceTableId, payBoxId)

      // A loja não aderiu ao parcelamento: nem consulta, nem cache. Esta é a única porta por onde
      // as opções entram na frente de venda — PDV e ATM —, então barrar aqui cobre os dois e,
      // principalmente, impede que a retaguarda offline ofereça um parcelamento que a loja
      // acabou de desligar.
      if (!rootGetters['pages/pdv/payBoxConfiguration/isInstallmentEnabled']) {
        commit('SET_RESOLUTION', { resolution: null, fromCache: false })
        return
      }

      if (!storeId || !saleValue || saleValue <= 0) {
        commit('SET_RESOLUTION', { resolution: null, fromCache: false })
        return
      }

      try {
        commit('SET_LOADING', true)

        const { data } = await axiosInstance.get('/api/sales/installment-options', {
          params: { storeId, priceTableId, saleValue, payBoxId },
        })

        const resolution = data?.hasApplicablePolicy ? data : null

        commit('SET_RESOLUTION', { resolution, fromCache: false })

        // Só o que veio da API entra no cache. Guardar uma resposta de cache faria a marca de
        // desatualização se perder ao longo das reutilizações.
        const cache = readCache()
        cache[scopeKey] = resolution
        writeCache(cache)
      } catch (error) {
        const cached = readCache()[scopeKey] || null

        commit('SET_RESOLUTION', { resolution: cached, fromCache: cached !== null })
      } finally {
        commit('SET_LOADING', false)
      }
    },

    cleanState({ commit }) {
      commit('CLEAN_STATE')
    },
  },
}
